import { z } from "zod"
import { TranslationKey, Translator } from "../i18n/translation-key"
import { Discount, percentDiscount } from "./discount"
import { PriceUtils } from "./price-utils"
import {
  BICYCLE_TYPES,
  ServiceBikeType,
  isNonBicycleType,
} from "./service-bike"

export enum UpsellCategoryGroup {
  LEGAL_REQUIREMENT = "LEGAL_REQUIREMENT",
  EVERYDAY = "EVERYDAY",
  COMFORT = "COMFORT",
  THEFT_PROTECTION = "THEFT_PROTECTION",
  TRAINING = "TRAINING",
  FALLBACK = "FALLBACK",
}

export const UPSELL_CATEGORY_GROUP_LABEL_KEYS: Record<
  UpsellCategoryGroup,
  TranslationKey
> = {
  [UpsellCategoryGroup.LEGAL_REQUIREMENT]:
    "common.upsell.categoryGroup.legalRequirement",
  [UpsellCategoryGroup.EVERYDAY]: "common.upsell.categoryGroup.everyday",
  [UpsellCategoryGroup.COMFORT]: "common.upsell.categoryGroup.comfort",
  [UpsellCategoryGroup.THEFT_PROTECTION]:
    "common.upsell.categoryGroup.theftProtection",
  [UpsellCategoryGroup.TRAINING]: "common.upsell.categoryGroup.training",
  [UpsellCategoryGroup.FALLBACK]: "common.upsell.categoryGroup.fallback",
}

/** Display order of the groups, legal requirements first. */
export const UPSELL_CATEGORY_GROUP_ORDER: UpsellCategoryGroup[] = [
  UpsellCategoryGroup.LEGAL_REQUIREMENT,
  UpsellCategoryGroup.EVERYDAY,
  UpsellCategoryGroup.COMFORT,
  UpsellCategoryGroup.THEFT_PROTECTION,
  UpsellCategoryGroup.TRAINING,
  UpsellCategoryGroup.FALLBACK,
]

/**
 * The groups offered right now. Everything is paused for now: empty means no
 * "Utrustning" step in the inspection, no "Tillval" step in new proposals and
 * no upsell tab in the CRM. Groups stay configured in admin and are switched
 * on by adding them here, starting with LEGAL_REQUIREMENT.
 */
export const ENABLED_UPSELL_CATEGORY_GROUPS: UpsellCategoryGroup[] = []

export const isUpsellCategoryGroupEnabled = (
  group: UpsellCategoryGroup,
): boolean => ENABLED_UPSELL_CATEGORY_GROUPS.includes(group)

export enum EquipmentStatus {
  PRESENT = "PRESENT",
  BROKEN = "BROKEN",
  MISSING = "MISSING",
}

export const EQUIPMENT_STATUS_LABEL_KEYS: Record<
  EquipmentStatus,
  TranslationKey
> = {
  [EquipmentStatus.PRESENT]: "common.upsell.equipmentStatus.present",
  [EquipmentStatus.BROKEN]: "common.upsell.equipmentStatus.broken",
  [EquipmentStatus.MISSING]: "common.upsell.equipmentStatus.missing",
}

/**
 * What a mechanic offers when every component of a category answered per
 * component is missing: a set covering all of them, or one product per
 * component (a front and a rear lamp).
 */
export enum UpsellOfferType {
  SET = "SET",
  PER_PART = "PER_PART",
}

export const UPSELL_OFFER_TYPE_LABEL_KEYS: Record<
  UpsellOfferType,
  TranslationKey
> = {
  [UpsellOfferType.SET]: "common.upsell.offerType.set",
  [UpsellOfferType.PER_PART]: "common.upsell.offerType.perPart",
}

export enum UpsellDiscountScope {
  LABOR_ONLY = "LABOR_ONLY",
  PRODUCT_ONLY = "PRODUCT_ONLY",
  BOTH = "BOTH",
}

export const UPSELL_DISCOUNT_SCOPE_LABEL_KEYS: Record<
  UpsellDiscountScope,
  TranslationKey
> = {
  [UpsellDiscountScope.LABOR_ONLY]: "common.upsell.discountScope.laborOnly",
  [UpsellDiscountScope.PRODUCT_ONLY]: "common.upsell.discountScope.productOnly",
  [UpsellDiscountScope.BOTH]: "common.upsell.discountScope.both",
}

/** A mechanic offers at most this many products per missing category. */
export const MAX_OFFERED_UPSELL_PRODUCTS = 3
/** Fewer offered products than this triggers the "offer two" warning. */
export const RECOMMENDED_MIN_OFFERED_UPSELL_PRODUCTS = 2

export const UpsellCategorySchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  group: z.nativeEnum(UpsellCategoryGroup),
  sortPriority: z.number().int(),
  bikeTypes: z.array(z.nativeEnum(ServiceBikeType)),
  electricBikeTypes: z.array(z.nativeEnum(ServiceBikeType)),
  partIds: z.array(z.string().uuid()),
  /** The components in `partIds`, named, for splitting products by type. */
  parts: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  /**
   * The mechanic answers each component on its own (Framlampa and Baklampa)
   * instead of the category as a whole.
   */
  isAnsweredPerPart: z.boolean(),
})
export type UpsellCategory = z.infer<typeof UpsellCategorySchema>

export const UpdateUpsellCategorySchema = z.object({
  name: z.string().trim().min(1),
  description: z
    .string()
    .trim()
    .nullable()
    .transform((value) => (value === "" ? null : value)),
  bikeTypes: z.array(z.nativeEnum(ServiceBikeType)),
  electricBikeTypes: z.array(z.nativeEnum(ServiceBikeType)),
  partIds: z.array(z.string().uuid()),
  isAnsweredPerPart: z.boolean(),
})
export type UpdateUpsellCategory = z.infer<typeof UpdateUpsellCategorySchema>

export const UpsellProductSchema = z.object({
  id: z.string().uuid(),
  categoryId: z.string().uuid(),
  title: z.string(),
  description: z.string(),
  imageUrl: z.string().nullable(),
  price: z.number(),
  fittingLaborPrice: z.number().nullable(),
  sortPriority: z.number().int(),
  catalogProductId: z.string().uuid().nullable(),
  catalogProductName: z.string().nullable(),
  /** The category component it covers on its own, null for a full set. */
  partId: z.string().uuid().nullable(),
  partName: z.string().nullable(),
  /** Stock from the linked catalog product, null when unknown. */
  stock: z.number().nullable(),
})
export type UpsellProduct = z.infer<typeof UpsellProductSchema>

export const UpsertUpsellProductSchema = z.object({
  categoryId: z
    .string()
    .uuid("common.validation.chooseCategory" satisfies TranslationKey),
  title: z
    .string()
    .trim()
    .min(1, "common.validation.enterTitle" satisfies TranslationKey),
  description: z.string().trim(),
  imageUrl: z.string().nullable(),
  price: z
    .number()
    .min(0, "common.validation.priceCannotBeNegative" satisfies TranslationKey),
  fittingLaborPrice: z
    .number()
    .int()
    .min(
      0,
      "common.validation.fittingPriceNotNegative" satisfies TranslationKey,
    )
    .nullable(),
  sortPriority: z.number().int(),
  catalogProductId: z.string().uuid().nullable(),
  partId: z.string().uuid().nullable(),
})
export type UpsertUpsellProduct = z.infer<typeof UpsertUpsellProductSchema>

export const UpsellDiscountSchema = z.object({
  percent: z.number().int().min(0).max(100),
  scope: z.nativeEnum(UpsellDiscountScope),
})
export type UpsellDiscount = z.infer<typeof UpsellDiscountSchema>

export const UpdateUpsellDiscountSchema = UpsellDiscountSchema
export type UpdateUpsellDiscount = z.infer<typeof UpdateUpsellDiscountSchema>

export const EquipmentPartStatusSchema = z.object({
  partId: z.string().uuid(),
  status: z.nativeEnum(EquipmentStatus),
})
export type EquipmentPartStatus = z.infer<typeof EquipmentPartStatusSchema>

export const EquipmentAnswerSchema = z
  .object({
    categoryId: z.string().uuid(),
    /**
     * The category's status. For a category answered per component it is
     * the summary of `partStatuses` (see `summarizeEquipmentPartStatuses`).
     */
    status: z.nativeEnum(EquipmentStatus),
    /** One per component, for a category answered per component. */
    partStatuses: z.array(EquipmentPartStatusSchema).default([]),
    /** Set or separate products, when all components are missing. */
    offerType: z.nativeEnum(UpsellOfferType).nullable().default(null),
    /** At most `MAX_OFFERED_UPSELL_PRODUCTS` per product type. */
    offeredProductIds: z.array(z.string().uuid()).default([]),
    /** One per product type with two or more offered products. */
    recommendedProductIds: z.array(z.string().uuid()).default([]),
  })
  .superRefine((answer, ctx) => {
    if (
      answer.recommendedProductIds.some(
        (id) => !answer.offeredProductIds.includes(id),
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "common.validation.recommendedProductNotOffered" satisfies TranslationKey,
        path: ["recommendedProductIds"],
      })
    }
    if (
      answer.partStatuses.length > 0 &&
      answer.status !== summarizeEquipmentPartStatuses(answer.partStatuses)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "common.validation.equipmentStatusMismatch" satisfies TranslationKey,
        path: ["status"],
      })
    }
  })
export type EquipmentAnswer = z.infer<typeof EquipmentAnswerSchema>

/** A component's answer: its own status, or the category's as a whole. */
export const getEquipmentPartStatus = (
  answer: Pick<EquipmentAnswer, "status" | "partStatuses">,
  partId: string,
): EquipmentStatus | null => {
  if (answer.partStatuses.length === 0) return answer.status
  return answer.partStatuses.find((p) => p.partId === partId)?.status ?? null
}

/**
 * The category status of a per-component answer: "Saknas" when anything is
 * missing (so it's offered), otherwise "Finns men trasig" when anything is
 * broken, otherwise "Finns".
 */
export const summarizeEquipmentPartStatuses = (
  partStatuses: EquipmentPartStatus[],
): EquipmentStatus => {
  const statuses = partStatuses.map((p) => p.status)
  if (statuses.includes(EquipmentStatus.MISSING)) return EquipmentStatus.MISSING
  if (statuses.includes(EquipmentStatus.BROKEN)) return EquipmentStatus.BROKEN
  return EquipmentStatus.PRESENT
}

/** Whether every component of the category has been answered. */
export const isEquipmentAnswerComplete = (
  answer: Pick<EquipmentAnswer, "partStatuses"> | undefined,
  category: Pick<UpsellCategory, "partIds" | "isAnsweredPerPart">,
): boolean => {
  if (!answer) return false
  // An answer for the whole category (also drafts saved before components
  // were answered on their own) covers every component.
  if (!category.isAnsweredPerPart || answer.partStatuses.length === 0) {
    return true
  }
  return category.partIds.every((partId) =>
    answer.partStatuses.some((p) => p.partId === partId),
  )
}

/** The components the answer says are missing. */
export const getMissingEquipmentPartIds = (
  answer: Pick<EquipmentAnswer, "status" | "partStatuses">,
  category: Pick<UpsellCategory, "partIds">,
): string[] =>
  category.partIds.filter(
    (partId) =>
      getEquipmentPartStatus(answer, partId) === EquipmentStatus.MISSING,
  )

/**
 * Whether the mechanic chooses between a set and separate products: only
 * when every component of a per-component category is missing.
 */
export const needsUpsellOfferType = (
  answer: Pick<EquipmentAnswer, "status" | "partStatuses">,
  category: Pick<UpsellCategory, "partIds" | "isAnsweredPerPart">,
): boolean =>
  category.isAnsweredPerPart &&
  answer.partStatuses.length > 0 &&
  category.partIds.length > 1 &&
  getMissingEquipmentPartIds(answer, category).length ===
    category.partIds.length

/**
 * The store products that may be offered for an answer. A category answered
 * as a whole offers all its products. Answered per component, a set is only
 * offered when everything is missing and the mechanic chose a set; otherwise
 * only products for the missing components are, so a working front lamp is
 * never replaced.
 */
export const getOfferableUpsellProducts = <
  T extends Pick<UpsellProduct, "categoryId" | "partId">,
>(
  answer: Pick<
    EquipmentAnswer,
    "categoryId" | "status" | "partStatuses" | "offerType"
  >,
  category: Pick<UpsellCategory, "id" | "partIds" | "isAnsweredPerPart">,
  products: T[],
): T[] => {
  const categoryProducts = products.filter(
    (product) => product.categoryId === category.id,
  )
  if (answer.status !== EquipmentStatus.MISSING) return []
  if (!category.isAnsweredPerPart || answer.partStatuses.length === 0) {
    return categoryProducts
  }
  if (needsUpsellOfferType(answer, category)) {
    if (answer.offerType === null) return []
    return categoryProducts.filter((product) =>
      answer.offerType === UpsellOfferType.SET
        ? product.partId === null
        : product.partId !== null,
    )
  }
  const missingPartIds = getMissingEquipmentPartIds(answer, category)
  return categoryProducts.filter(
    (product) =>
      product.partId !== null && missingPartIds.includes(product.partId),
  )
}

/**
 * The default choice when everything is missing: a set if the store has one,
 * otherwise separate products.
 */
export const getDefaultUpsellOfferType = (
  category: Pick<UpsellCategory, "id">,
  products: Pick<UpsellProduct, "categoryId" | "partId">[],
): UpsellOfferType =>
  products.some(
    (product) => product.categoryId === category.id && product.partId === null,
  )
    ? UpsellOfferType.SET
    : UpsellOfferType.PER_PART

/**
 * Every component answered "Saknas": the bike does not have them, so they have
 * no condition to grade and no work to do.
 */
export const getAllMissingEquipmentPartIds = (
  answers: Pick<EquipmentAnswer, "categoryId" | "status" | "partStatuses">[],
  categories: Pick<UpsellCategory, "id" | "partIds">[],
): Set<string> => {
  const answerByCategory = new Map(
    answers.map((answer) => [answer.categoryId, answer]),
  )
  return new Set(
    categories.flatMap((category) => {
      const answer = answerByCategory.get(category.id)
      return answer ? getMissingEquipmentPartIds(answer, category) : []
    }),
  )
}

/**
 * Components that must not carry work: those answered as "Finns" or
 * "Saknas". Work on equipment is only done when it is broken, so a component
 * shared with a category answered "Finns men trasig" keeps its work.
 */
export const getUnrepairedEquipmentPartIds = (
  answers: Pick<EquipmentAnswer, "categoryId" | "status" | "partStatuses">[],
  categories: Pick<UpsellCategory, "id" | "partIds">[],
): Set<string> => {
  const answerByCategory = new Map(
    answers.map((answer) => [answer.categoryId, answer]),
  )
  const answeredParts = categories.flatMap((category) => {
    const answer = answerByCategory.get(category.id)
    if (!answer) return []
    return category.partIds.flatMap((partId) => {
      const status = getEquipmentPartStatus(answer, partId)
      return status ? [{ partId, status }] : []
    })
  })
  const brokenPartIds = new Set(
    answeredParts
      .filter((part) => part.status === EquipmentStatus.BROKEN)
      .map((part) => part.partId),
  )
  return new Set(
    answeredParts
      .filter((part) => !brokenPartIds.has(part.partId))
      .map((part) => part.partId),
  )
}

/**
 * A product covers its whole category (a set, e.g. lamps front and rear) or
 * one of the category's components (only the front lamp). Two products in the
 * same category clash when either is a set or both cover the same component,
 * so a customer can pick one set, or one product per component.
 */
export const doUpsellProductsConflict = (
  a: { partId: string | null },
  b: { partId: string | null },
): boolean => a.partId === null || b.partId === null || a.partId === b.partId

export interface UpsellProductGroup<T> {
  /** Null for the sets. */
  partId: string | null
  partName: string | null
  products: T[]
}

/** Products split by type: sets first, then each component as first seen. */
export const groupUpsellProductsByPart = <
  T extends { partId: string | null; partName: string | null },
>(
  products: T[],
): UpsellProductGroup<T>[] => {
  const groups: UpsellProductGroup<T>[] = []
  for (const product of products) {
    const group = groups.find((g) => g.partId === product.partId)
    if (group) {
      group.products.push(product)
    } else {
      groups.push({
        partId: product.partId,
        partName: product.partName,
        products: [product],
      })
    }
  }
  return groups.sort(
    (a, b) => Number(a.partId !== null) - Number(b.partId !== null),
  )
}

/**
 * Caps the offered products at `MAX_OFFERED_UPSELL_PRODUCTS` per type,
 * keeping the most recently added. Ids of unknown products are dropped.
 */
export const limitOfferedUpsellProductIds = (
  offeredProductIds: string[],
  products: Pick<UpsellProduct, "id" | "partId">[],
): string[] => {
  const countByPart = new Map<string | null, number>()
  const kept: string[] = []
  for (const id of [...offeredProductIds].reverse()) {
    const product = products.find((p) => p.id === id)
    if (!product) continue
    const count = countByPart.get(product.partId) ?? 0
    if (count >= MAX_OFFERED_UPSELL_PRODUCTS) continue
    countByPart.set(product.partId, count + 1)
    kept.push(id)
  }
  return kept.reverse()
}

/**
 * The offered products the mechanic recommends, one per type with two or
 * more offered products. A current pick is kept while it's still offered,
 * otherwise the type's most expensive product (fitting included) is picked.
 */
export const resolveRecommendedUpsellProductIds = (
  offeredProductIds: string[],
  currentRecommendedIds: string[],
  products: Pick<
    UpsellProduct,
    "id" | "price" | "fittingLaborPrice" | "partId" | "partName"
  >[],
): string[] => {
  const offered = offeredProductIds.flatMap((id) => {
    const product = products.find((p) => p.id === id)
    return product ? [product] : []
  })
  return groupUpsellProductsByPart(offered).flatMap((group) => {
    if (group.products.length < 2) return []
    const current = group.products.find((p) =>
      currentRecommendedIds.includes(p.id),
    )
    if (current) return [current.id]
    const mostExpensive = group.products.reduce((best, product) =>
      product.price + (product.fittingLaborPrice ?? 0) >
      best.price + (best.fittingLaborPrice ?? 0)
        ? product
        : best,
    )
    return [mostExpensive.id]
  })
}

/** Everything the inspection's "Utrustning" step needs. */
export interface UpsellInspectionData {
  /** Empty when the store has no products, which skips the step. */
  categories: UpsellCategory[]
  products: UpsellProduct[]
  draftEquipment: EquipmentAnswer[]
}

/** One product offered to the customer, snapshotted when the proposal was sent. */
export const ServiceProposalUpsellOfferProductSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string(),
  imageUrl: z.string().nullable(),
  price: z.number(),
  fittingLaborPrice: z.number().nullable(),
  /** The category component it covers on its own, null for a full set. */
  partId: z.string().uuid().nullable().default(null),
  partName: z.string().nullable().default(null),
  /** The product the mechanic recommends among its type in this offer. */
  isRecommended: z.boolean(),
  /** Live stock, filled in when the customer opens the proposal. */
  stock: z.number().nullable().default(null),
})
export type ServiceProposalUpsellOfferProduct = z.infer<
  typeof ServiceProposalUpsellOfferProductSchema
>

export const ServiceProposalUpsellOfferSchema = z.object({
  id: z.string().uuid(),
  categoryId: z.string().uuid(),
  categoryName: z.string(),
  categoryDescription: z.string().nullable(),
  group: z.nativeEnum(UpsellCategoryGroup),
  isFallback: z.boolean(),
  products: z.array(ServiceProposalUpsellOfferProductSchema),
})
export type ServiceProposalUpsellOffer = z.infer<
  typeof ServiceProposalUpsellOfferSchema
>

export const ServiceProposalUpsellSchema = z.object({
  offers: z.array(ServiceProposalUpsellOfferSchema),
  discount: UpsellDiscountSchema.nullable(),
})
export type ServiceProposalUpsell = z.infer<typeof ServiceProposalUpsellSchema>

/**
 * The categories a mechanic answers for this bike. Electric bikes use their
 * own list. Only enabled groups are asked about, and fallback categories never
 * are — they only fill the customer's step when nothing was missing.
 */
export const getApplicableUpsellCategories = <
  T extends Pick<
    UpsellCategory,
    "group" | "bikeTypes" | "electricBikeTypes" | "sortPriority"
  >,
>(
  categories: T[],
  bike: { type: ServiceBikeType; isElectric: boolean | null },
): T[] => {
  if (isNonBicycleType(bike.type)) return []
  return categories
    .filter((category) => category.group !== UpsellCategoryGroup.FALLBACK)
    .filter((category) => isUpsellCategoryGroupEnabled(category.group))
    .filter((category) =>
      (bike.isElectric
        ? category.electricBikeTypes
        : category.bikeTypes
      ).includes(bike.type),
    )
    .sort(compareUpsellCategories)
}

export const compareUpsellCategories = (
  a: Pick<UpsellCategory, "group" | "sortPriority">,
  b: Pick<UpsellCategory, "group" | "sortPriority">,
): number =>
  UPSELL_CATEGORY_GROUP_ORDER.indexOf(a.group) -
    UPSELL_CATEGORY_GROUP_ORDER.indexOf(b.group) ||
  a.sortPriority - b.sortPriority

/** Bike types an admin can map categories to. */
export const UPSELL_BIKE_TYPES: ServiceBikeType[] = [
  ...BICYCLE_TYPES,
  ServiceBikeType.OTHER,
]

/**
 * The store's "here and now" discount split into the labor and product
 * discounts an upsell line carries, depending on its scope.
 */
export const getUpsellDiscounts = (
  discount: UpsellDiscount | null,
): { laborDiscount: Discount | null; productDiscount: Discount | null } => {
  if (discount == null || discount.percent === 0) {
    return { laborDiscount: null, productDiscount: null }
  }
  const asDiscount = percentDiscount(discount.percent)
  return {
    laborDiscount:
      discount.scope === UpsellDiscountScope.PRODUCT_ONLY ? null : asDiscount,
    productDiscount:
      discount.scope === UpsellDiscountScope.LABOR_ONLY ? null : asDiscount,
  }
}

/** The work-order title of an accepted upsell. */
export const getUpsellActionTitle = (
  t: Translator,
  categoryName: string,
  productTitle: string,
): string => t("common.upsell.actionTitle", { categoryName, productTitle })

export interface UpsellProductPricing {
  productPrice: number
  productPriceNow: number
  laborPrice: number
  laborPriceNow: number
  total: number
  totalNow: number
  saving: number
}

/**
 * What an upsell costs at ordinary price and with the "here and now"
 * discount. The same numbers are used on the customer page, the receipt and
 * when the accepted upsell is written to the job, so they always agree.
 */
export const getUpsellProductPricing = (
  product: Pick<
    ServiceProposalUpsellOfferProduct,
    "price" | "fittingLaborPrice"
  >,
  discount: UpsellDiscount | null,
): UpsellProductPricing => {
  const laborPrice = product.fittingLaborPrice ?? 0
  const productPrice = product.price
  const { laborDiscount, productDiscount } = getUpsellDiscounts(discount)
  const laborPriceNow = PriceUtils.applyDiscount(laborPrice, laborDiscount)
  const productPriceNow = PriceUtils.applyDiscount(
    productPrice,
    productDiscount,
  )
  const total = laborPrice + productPrice
  const totalNow = laborPriceNow + productPriceNow
  return {
    productPrice,
    productPriceNow,
    laborPrice,
    laborPriceNow,
    total,
    totalNow,
    saving: total - totalNow,
  }
}

/**
 * Toggles one offered product in the customer's selection. Picking a product
 * replaces the picks it clashes with in the same category (see
 * `doUpsellProductsConflict`), picking the same one again removes it.
 */
export const toggleUpsellProductSelection = (
  selectedProductIds: string[],
  offers: Pick<ServiceProposalUpsellOffer, "products">[],
  productId: string,
): string[] => {
  if (selectedProductIds.includes(productId)) {
    return selectedProductIds.filter((id) => id !== productId)
  }
  const offer = offers.find((o) => o.products.some((p) => p.id === productId))
  const product = offer?.products.find((p) => p.id === productId)
  if (!offer || !product) return selectedProductIds
  const clashingIds = new Set(
    offer.products
      .filter((p) => doUpsellProductsConflict(p, product))
      .map((p) => p.id),
  )
  return [...selectedProductIds.filter((id) => !clashingIds.has(id)), productId]
}

/**
 * The selected offer products. Unknown ids are dropped, and of two clashing
 * picks in one offer only the first is kept.
 */
export const resolveSelectedUpsellProducts = (
  upsell: ServiceProposalUpsell | null | undefined,
  selectedProductIds: string[],
): {
  offer: ServiceProposalUpsellOffer
  product: ServiceProposalUpsellOfferProduct
}[] => {
  if (!upsell) return []
  return upsell.offers.flatMap((offer) => {
    const picked: ServiceProposalUpsellOfferProduct[] = []
    for (const id of selectedProductIds) {
      const product = offer.products.find((p) => p.id === id)
      if (
        product &&
        !picked.some(
          (p) => p.id === product.id || doUpsellProductsConflict(p, product),
        )
      ) {
        picked.push(product)
      }
    }
    return picked.map((product) => ({ offer, product }))
  })
}
